/**
 * Trainova i18n bootstrap (Phase 1).
 *
 * Scope: shared foundation + Swedish/English resources. Only the login flow
 * is translated in this phase. Persistence via `localStorage` under the key
 * `trainova.language`. No auto-detection from `navigator.language` yet.
 *
 * Fallback chain: <selected> → sv → key (dev-only warning).
 */
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

import svCommon from './locales/sv/common.json';
import svAuth from './locales/sv/auth.json';
import svOnboarding from './locales/sv/onboarding.json';
import svErrors from './locales/sv/errors.json';
import svNavigation from './locales/sv/navigation.json';
import svSettings from './locales/sv/settings.json';
import svDashboard from './locales/sv/dashboard.json';
import svCalendar from './locales/sv/calendar.json';
import svTraining from './locales/sv/training.json';
import svExercises from './locales/sv/exercises.json';
import svMatches from './locales/sv/matches.json';
import svDevelopment from './locales/sv/development.json';
import svChat from './locales/sv/chat.json';
import svProfile from './locales/sv/profile.json';
import svNotifications from './locales/sv/notifications.json';
import svTeamAdmin from './locales/sv/team_admin.json';import svOrganization from './locales/sv/organization.json';
import svAppAdmin from './locales/sv/app_admin.json';
import svLanding from './locales/sv/landing.json';
import svStatistics from './locales/sv/statistics.json';
import svPosts from './locales/sv/posts.json';
import svSelfOnboarding from './locales/sv/self_onboarding.json';
import enCommon from './locales/en/common.json';
import enAuth from './locales/en/auth.json';
import enOnboarding from './locales/en/onboarding.json';
import enErrors from './locales/en/errors.json';
import enNavigation from './locales/en/navigation.json';
import enSettings from './locales/en/settings.json';
import enDashboard from './locales/en/dashboard.json';
import enCalendar from './locales/en/calendar.json';
import enTraining from './locales/en/training.json';
import enExercises from './locales/en/exercises.json';
import enMatches from './locales/en/matches.json';
import enDevelopment from './locales/en/development.json';
import enChat from './locales/en/chat.json';
import enProfile from './locales/en/profile.json';
import enNotifications from './locales/en/notifications.json';
import enTeamAdmin from './locales/en/team_admin.json';
import enOrganization from './locales/en/organization.json';
import enAppAdmin from './locales/en/app_admin.json';
import enLanding from './locales/en/landing.json';
import enStatistics from './locales/en/statistics.json';
import enPosts from './locales/en/posts.json';
import enSelfOnboarding from './locales/en/self_onboarding.json';

export const SUPPORTED_LANGUAGES = ['sv', 'en'];
export const FALLBACK_LANGUAGE = 'sv';
export const STORAGE_KEY = 'trainova.language';

export function readStoredLanguage() {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw && SUPPORTED_LANGUAGES.includes(raw)) return raw;
  } catch {
    /* localStorage unavailable — fall through */
  }
  return FALLBACK_LANGUAGE;
}

export function persistLanguage(lng) {
  try {
    if (SUPPORTED_LANGUAGES.includes(lng)) {
      window.localStorage.setItem(STORAGE_KEY, lng);
    }
  } catch {
    /* ignore quota / privacy-mode errors */
  }
}

const initialLng = readStoredLanguage();

i18n
  .use(initReactI18next)
  .init({
    resources: {
      sv: { common: svCommon, auth: svAuth, onboarding: svOnboarding, errors: svErrors, navigation: svNavigation, settings: svSettings, dashboard: svDashboard, calendar: svCalendar, training: svTraining, exercises: svExercises, matches: svMatches, development: svDevelopment, chat: svChat, profile: svProfile, notifications: svNotifications, team_admin: svTeamAdmin, organization: svOrganization, app_admin: svAppAdmin, landing: svLanding, statistics: svStatistics, posts: svPosts, self_onboarding: svSelfOnboarding },
      en: { common: enCommon, auth: enAuth, onboarding: enOnboarding, errors: enErrors, navigation: enNavigation, settings: enSettings, dashboard: enDashboard, calendar: enCalendar, training: enTraining, exercises: enExercises, matches: enMatches, development: enDevelopment, chat: enChat, profile: enProfile, notifications: enNotifications, team_admin: enTeamAdmin, organization: enOrganization, app_admin: enAppAdmin, landing: enLanding, statistics: enStatistics, posts: enPosts, self_onboarding: enSelfOnboarding },
    },
    lng: initialLng,
    fallbackLng: FALLBACK_LANGUAGE,
    supportedLngs: SUPPORTED_LANGUAGES,
    ns: ['common', 'auth', 'onboarding', 'errors', 'navigation', 'settings', 'dashboard', 'calendar', 'training', 'exercises', 'matches', 'development', 'chat', 'profile', 'notifications', 'team_admin', 'organization', 'app_admin', 'landing', 'statistics', 'posts', 'self_onboarding'],
    defaultNS: 'common',
    interpolation: { escapeValue: false },
    returnEmptyString: false,
    saveMissing: false,
    // Dev-only warning so we notice untranslated keys during Phase 2+.
    missingKeyHandler:
      typeof process !== 'undefined' && process.env?.NODE_ENV !== 'production'
        ? (lngs, ns, key) => {
            console.warn(`[i18n] missing key "${ns}:${key}" for ${lngs.join(',')}`);
          }
        : undefined,
    react: { useSuspense: false },
  });

// Sync `<html lang>` for a11y + persist explicit changes.
i18n.on('languageChanged', (lng) => {
  try {
    document.documentElement.setAttribute('lang', lng);
  } catch { /* noop (SSR / early boot) */ }
  persistLanguage(lng);
});
try {
  document.documentElement.setAttribute('lang', initialLng);
} catch { /* noop */ }

export default i18n;
